import { Component, DestroyRef, OnInit, Signal, computed, signal } from '@angular/core';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';

import { AccessService } from './../../../services/access/access.service';
import { ButtonModule } from 'primeng/button';
import { CardComponent } from '../../../shared/components/card/card.component';
import { Location } from '../../../shared/models/location.model';
import { LocationService } from '../../../services/location/location.service';
import { MessageService } from 'primeng/api';
import { Pagination } from '../../../shared/models/pagination.model';
import { Pet } from '../../../shared/models/pet.model';
import { PetCardComponent } from '../../../components/pet-card/pet-card.component';
import { PetModalComponent } from '../../../components/modals/pet-modal/pet-modal.component';
import { PetService } from '../../../services/pet/pet.service';
import { Router } from '@angular/router';
import { User } from '../../../shared/models/user.model';
import { UserPetLocationComponent } from './sections/user-pet-location/user-pet-location.component';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-home',
  imports: [
    CardComponent,
    ButtonModule,
    PetCardComponent,
    PetModalComponent,
    UserPetLocationComponent,
    PaginatorModule
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss'
})
export class HomeComponent implements OnInit {
  readonly user: Signal<User | null>;

  readonly isPetModalVisible = signal<boolean>(false);
  readonly isPetCardLoading = signal<boolean>(false);

  readonly isLocationModalVisible = signal<boolean>(false);
  readonly isLocationCardLoading = signal<boolean>(false);
  readonly pets = signal<Pet[]>([]);
  readonly totalPets = signal<number>(0);

  readonly locations = signal<Pagination<Location>>({
    rows: [],
    count: 0,
  });
  readonly totalLocations = signal<number>(0);

  private readonly greeting = signal<string>('');
  readonly userMessage = computed(() => `${this.greeting()}, ${this.user()!.name}!`);
  private greetingInterval: any = null;

  constructor(private readonly accessService: AccessService,
    private readonly petService: PetService,
    private readonly locationService: LocationService,
    private readonly messageService: MessageService,
    private readonly router: Router,
    private readonly destroyRef: DestroyRef) {
    this.user = this.accessService.userInfo;
  }

  ngOnInit(): void {
    this.triggerGreeting();
    this.fetchPets(0);
    this.fetchUserPetsLocations(0);
    this.destroyRef.onDestroy(() => {
      clearInterval(this.greetingInterval);
    });
  }

  onSelectPet(selectedPet: Pet): void {
    this.isPetCardLoading.set(true);
    this.router.navigate(['pet', selectedPet.id])
  }

  onOpenPetModal(): void {
    this.isPetModalVisible.set(true);
  }

  onPageChange(event: PaginatorState, pets: boolean = true): void {
    pets ? this.fetchPets(event.page ?? 0) : this.fetchUserPetsLocations(event.page ?? 0);
  }

  fetchPets(page: number): void {
    this.isPetCardLoading.set(true);
    this.petService.paginatePetsByUser(this.user()!.id!, page)
      .pipe(finalize(() => this.isPetCardLoading.set(false)))
      .subscribe({
        next: (petPage: Pagination<Pet>) => {
          this.pets.set(petPage.rows);
          this.totalPets.set(petPage.count);
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Falha ao buscar pets do usuário!',
            life: 4000
          });
        }
      });
  }

    fetchUserPetsLocations(page: number): void {
    this.isLocationCardLoading.set(true);
    this.locationService.paginateRecentLocationByUserId(this.user()!.id!, page)
      .pipe(finalize(() => this.isLocationCardLoading.set(false)))
      .subscribe({
        next: (locationPage: Pagination<Location>) => {
          this.locations.set(locationPage);
          this.totalPets.set(locationPage.count);
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Falha ao buscar localizações dos pets do usuário!',
            life: 4000
          });
        }
      });
  }

  private triggerGreeting(): void {
    this.getPeriod();
    this.greetingInterval = setInterval(() => {
      this.getPeriod();
    }, 1000 * 60 * 60); // 1 hour
  }

  private getPeriod(): void {
    const now = new Date();
    const hours = now.getHours();
    if (hours < 12) {
      this.greeting.set('Bom dia');
    } else if (hours < 18) {
      this.greeting.set('Boa tarde');
    } else {
      this.greeting.set('Boa noite');
    }
  }
}
